import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { getUserExchanges } from '../../services/UserExchangesService';
import { getProviders } from '../../services/ExchangeService';
import { getAutomationsCounts } from '../../services/AutomationsService';
import { getEnvironmentPolicy } from '../../services/SettingsService';
import {
    getCurrentEnvironment, setCurrentEnvironment, subscribeToCurrentEnvironment
} from '../../utils/CurrentEnvironmentStorage';


const EMPTY_COUNTS = {
    production: { active: 0, total: 0 },
    test: { active: 0, total: 0 }
};

/**
 * Toggle global de ambiente da corretora primary.
 * Mostra só ambientes com credenciais cadastradas (availableEnvironments
 * vindos do backend). Caso o env atual deixe de estar disponível (ex:
 * usuário trocou a primary), corrige automaticamente.
 */
function CurrentEnvironmentToggle() {
    const [primary, setPrimary] = useState(null);
    const [exchangeLabel, setExchangeLabel] = useState('');
    const [env, setEnv] = useState(getCurrentEnvironment());
    const [counts, setCounts] = useState(EMPTY_COUNTS);
    const [loaded, setLoaded] = useState(false);
    const [policy, setPolicy] = useState(null); // { systemMode, effectiveMode, allowed, locked, source }
    const location = useLocation();

    function reload() {
        getUserExchanges()
            .then(rows => {
                const p = (rows || []).find(r => r.isPrimary && r.isActive) || null;
                setPrimary(p);
                setLoaded(true);
                const available = (p && p.availableEnvironments) || [];
                const current = getCurrentEnvironment();
                if (available.length && !available.includes(current)) {
                    setCurrentEnvironment(available[0]);
                    setEnv(available[0]);
                }
            })
            .catch(() => setLoaded(true));
    }

    function reloadCounts() {
        getAutomationsCounts()
            .then(c => setCounts(c || EMPTY_COUNTS))
            .catch(() => setCounts(EMPTY_COUNTS));
    }

    useEffect(() => {
        reload();
        getProviders()
            .then(({ exchanges }) => {
                setExchangeLabel((exchanges || []).reduce((acc, e) => ({ ...acc, [e.name]: e.label }), {}));
            })
            .catch(() => {});
        getEnvironmentPolicy()
            .then(p => {
                setPolicy(p);
                // Se a política trava num ambiente específico, força o toggle local.
                if (p && p.locked && p.effectiveMode) {
                    const forcedEnv = p.effectiveMode === 'TEST_ONLY' ? 'test' : p.effectiveMode === 'PRODUCTION_ONLY' ? 'production' : null;
                    if (forcedEnv) {
                        setCurrentEnvironment(forcedEnv);
                        setEnv(forcedEnv);
                    }
                }
            })
            .catch(() => {});
        const unsub = subscribeToCurrentEnvironment(setEnv);
        return unsub;
    }, []);

    // Re-fetcha contagens em cada mudança de rota — barato e cobre os casos
    // de start/stop/delete/promote feitos em qualquer tela.
    useEffect(() => { reloadCounts(); }, [location.pathname]);

    function onChange(next) {
        // Respeita a política: só permite trocar para ambientes permitidos.
        if (policy && policy.locked) return; // admin fixou — não permite troca
        if (policy && policy.allowed && !policy.allowed.includes(next)) return;
        setCurrentEnvironment(next);
        setEnv(next);
    }

    if (!loaded) return null;

    if (!primary) {
        return (
            <li className='nav-item mb-3 text-center'>
                <small className='sidebar-text text-muted d-block mb-1' style={{ fontSize: '0.75em' }}>
                    Nenhuma corretora configurada
                </small>
                <Link to='/settings' className='btn btn-sm btn-outline-warning' style={{ fontSize: '0.75em' }}>
                    Configurar corretora
                </Link>
            </li>
        );
    }

    const available = primary.availableEnvironments || [];
    const label = (exchangeLabel && exchangeLabel[primary.exchangeName]) || primary.exchangeName;
    const noneConfigured = available.length === 0;
    const prodHasCreds = available.includes('production');
    const testHasCreds = available.includes('test');

    // Se admin travou o modo, exibe apenas um badge estático.
    const policyLocked = policy && policy.locked;
    const lockedEnv = policyLocked
        ? (policy.effectiveMode === 'TEST_ONLY' ? 'test' : 'production')
        : null;
    const lockedLabel = lockedEnv === 'test' ? '🟡 Teste' : '🟢 Prod';
    const lockedBadgeClass = lockedEnv === 'test' ? 'btn-warning text-dark' : 'btn-success';

    // Estilo do botão Prod: verde quando ativo+credencial, laranja quando ativo sem credencial,
    // outline quando inativo.
    function prodBtnClass() {
        if (env !== 'production') return 'btn-outline-light';
        return prodHasCreds ? 'btn-success' : 'btn-warning text-dark';
    }

    // Filtra os ambientes que a política permite (se não estiver travada).
    const policyAllowed = (policy && !policy.locked && policy.allowed) || null;
    const prodPolicyBlocked = policyAllowed && !policyAllowed.includes('production');
    const testPolicyBlocked = policyAllowed && !policyAllowed.includes('test');

    return (
        <li className='nav-item mb-3 text-center'>
            <small className='sidebar-text text-muted d-block mb-1' style={{ fontSize: '0.75em' }}>
                Corretora: <strong className='text-light'>{label}</strong>
            </small>
            {noneConfigured && (
                <Link to='/settings' className='btn btn-sm btn-outline-warning' style={{ fontSize: '0.7em' }}>
                    Adicionar credenciais
                </Link>
            )}
            {!noneConfigured && policyLocked && (
                <div>
                    <span className={`btn btn-sm ${lockedBadgeClass}`} style={{ fontSize: '0.75em', cursor: 'default' }}>
                        {lockedLabel} <span className='badge bg-dark ms-1'>{lockedEnv === 'test' ? counts.test.active : counts.production.active}</span>
                    </span>
                    <small className='sidebar-text d-block mt-1 text-muted' style={{ fontSize: '0.7em' }}>
                        Fixado pelo administrador
                    </small>
                </div>
            )}
            {!noneConfigured && !policyLocked && (
                <>
                    <div className='btn-group btn-group-sm' role='group' aria-label='Ambiente da corretora'>
                        <button type='button'
                            className={`btn ${prodBtnClass()}`}
                            style={{ fontSize: '0.75em' }}
                            onClick={() => onChange('production')}
                            disabled={prodPolicyBlocked}
                            title={prodPolicyBlocked
                                ? 'Produção bloqueada pela política do sistema'
                                : prodHasCreds
                                    ? `Produção · ${counts.production.active} ativa(s) de ${counts.production.total}`
                                    : 'Sem credenciais de produção — acesso somente leitura (sem trades)'}>
                            🟢 Prod <span className='badge bg-dark ms-1'>{counts.production.active}</span>
                        </button>
                        <button type='button'
                            className={`btn ${env === 'test' ? 'btn-warning text-dark' : 'btn-outline-light'}`}
                            style={{ fontSize: '0.75em' }}
                            disabled={!testHasCreds || testPolicyBlocked}
                            onClick={() => onChange('test')}
                            title={testPolicyBlocked
                                ? 'Testnet bloqueada pela política do sistema'
                                : testHasCreds
                                    ? `Testnet · ${counts.test.active} ativa(s) de ${counts.test.total}`
                                    : 'Configure as credenciais de testnet em Configurações para liberar'}>
                            🟡 Teste <span className='badge bg-dark ms-1'>{counts.test.active}</span>
                        </button>
                    </div>
                    {env === 'production' && !prodHasCreds && (
                        <small className='sidebar-text d-block mt-1 text-warning' style={{ fontSize: '0.7em' }}>
                            ⚠ Só leitura — <Link to='/settings' className='text-warning'>adicionar credenciais</Link>
                        </small>
                    )}
                </>
            )}
        </li>
    );
}

export default CurrentEnvironmentToggle;
